import { IBlock } from '@core/typings';
import {
	getAdapterAttributesString,
	getChildIdx,
	getPreviewClassName,
} from '@core/utils';
import { getPlaceholder } from '@core/utils/getPlaceholder';
import { getMergeTagImageUrl } from '@core/utils/getMergeTagImageUrl';
import { getProxiedImageUrl } from '@core/utils/getProxiedImageUrl';
import React from 'react';
import { BlockRenderer } from './BlockRenderer';

export function BasicBlock(props: {
	params: Parameters<IBlock['render']>[0];
	tag: string;
	children?: React.ReactNode;
}) {
	const {
		params,
		params: { data, idx, children: children2, mode },
		tag,
		children,
	} = props;

	const placeholder = data.children.length === 0 && getPlaceholder(params);

	let content = children || children2;

	if (
		(!content || (Array.isArray(content) && content.length === 0)) &&
		data.children.length === 0
	) {
		content = placeholder;
	}

	if (mode === 'testing' && tag === 'mj-image') {
		let url = data.attributes.src;

		// Use the utility to handle merge tag images
		// This will show a placeholder with the merge tag name instead of a broken image
		const processedUrl = getMergeTagImageUrl(url, mode);

		if (processedUrl !== url) {
			// URL was transformed (either merge tag or empty), use the processed URL
			const modifiedAttributes = { ...data.attributes, src: processedUrl };
			const modifiedData = { ...data, attributes: modifiedAttributes };
			const modifiedParams = { ...params, data: modifiedData };
			return (
				<>
					{`<${tag} ${getAdapterAttributesString(modifiedParams)}>`}
					{`</${tag}>`}
				</>
			);
		}
	}

	// Handle background-url for mj-hero and mj-wrapper
	if (mode === 'testing' && (tag === 'mj-hero' || tag === 'mj-wrapper')) {
		let url = data.attributes['background-url'];

		if (url) {
			const processedUrl = getMergeTagImageUrl(url, mode);

			// If URL was processed (merge tag detected), update the attributes
			if (processedUrl !== url) {
				const modifiedAttributes = { ...data.attributes, 'background-url': processedUrl };
				const modifiedData = { ...data, attributes: modifiedAttributes };
				const modifiedParams = { ...params, data: modifiedData };
				return (
					<>
						{`<${tag} ${getAdapterAttributesString(modifiedParams)}>`}
						{content ||
							data.children.map((child, index) => (
								<BlockRenderer
									key={index}
									{...params}
									idx={idx ? getChildIdx(idx, index) : null}
									data={child}
								/>
							))}
						{`</${tag}>`}
					</>
				);
			}
			// For base64/blob URLs and regular URLs, let them pass through normally
		}
	}

	// In testing mode, mj-raw does not preserve css-class attributes in output HTML.
	// Wrap raw content in a div that carries selection classes so hidden and selection work.
	if (mode === 'testing' && tag === 'mj-raw') {
		const className =
			getPreviewClassName(idx || null, data.type) +
			((data as any)?.data?.hidden ? ' is-hidden' : '');
		return (
			<>
				{`<${tag}>`}
				{`<div class="${className}">`}
				{content as any}
				{`</div>`}
				{`</${tag}>`}
			</>
		);
	}

	// Generic hidden handling in testing mode: render an inner placeholder div
	// that carries preview/selection classes and suppresses children.
	if (mode === 'testing' && (data as any)?.data?.hidden) {
		const className = getPreviewClassName(idx || null, data.type) + ' is-hidden';
		return (
			<>
				{`<${tag} ${getAdapterAttributesString(params)}>`}
				{`<div class="${className}"></div>`}
				{`</${tag}>`}
			</>
		);
	}

	// Apply image proxy to optimize image delivery for both editor and email output
	let modifiedParams = params;

	// Handle mj-image src proxy
	if (tag === 'mj-image') {
		const width = data.attributes.width || '600px';
		const height = data.attributes.height || 'auto';
		const originalSrc = data.attributes.src;

		// A dynamic image field fixes its fit by field type (logo=contain,
		// photo/circle=cover) and that always wins — the box is sized for an
		// asset we have not seen, so stretching it to fit would distort every
		// record differently. The Image block computes this and hands it down on
		// the derived data; it is never persisted on the block.
		const dynamicFit = (data as any)?.data?.value?.dynamicImageFit as
			| 'contain'
			| 'cover'
			| undefined;

		// Otherwise: pinning BOTH width and height means honor them literally
		// (fit=fill); a single fixed dimension scales the other proportionally.
		const isFixedPx = (val: string) => /^\d+(?:\.\d+)?(?:px)?$/i.test(val.trim());
		const fit =
			dynamicFit || (isFixedPx(width) && isFixedPx(height) ? 'fill' : 'contain');

		// REVERT MARKER B — the 6th argument is the only edit here. Passing
		// false restores the previous behaviour (PNGs skip the proxy entirely).
		// A declared dynamic fit is a hard requirement, so those PNGs must be
		// transformed rather than passed through at their native shape.
		const proxiedSrc = getProxiedImageUrl(
			originalSrc,
			width,
			height,
			undefined,
			fit,
			Boolean(dynamicFit),
		);

		if (proxiedSrc !== originalSrc) {
			const modifiedAttributes = { ...data.attributes, src: proxiedSrc };
			const modifiedData = { ...data, attributes: modifiedAttributes };
			modifiedParams = { ...params, data: modifiedData };
		}
	}

	// Handle mj-hero and mj-wrapper background-url proxy
	if (tag === 'mj-hero' || tag === 'mj-wrapper') {
		const originalBgUrl = data.attributes['background-url'];

		if (originalBgUrl) {
			let width = '600px';
			let height = 'auto';

			// mj-hero has background-width and background-height attributes
			if (tag === 'mj-hero') {
				width = data.attributes['background-width'] || '600px';
				height = data.attributes['background-height'] || 'auto';
			}

			const proxiedBgUrl = getProxiedImageUrl(originalBgUrl, width, height);

			if (proxiedBgUrl !== originalBgUrl) {
				const modifiedAttributes = { ...data.attributes, 'background-url': proxiedBgUrl };
				const modifiedData = { ...data, attributes: modifiedAttributes };
				modifiedParams = { ...params, data: modifiedData };
			}
		}
	}

	return (
		<>
			{`<${tag} ${getAdapterAttributesString(modifiedParams)}>`}
			{content ||
				data.children.map((child, index) => (
					<BlockRenderer
						key={index}
						{...modifiedParams}
						idx={idx ? getChildIdx(idx, index) : null}
						data={child}
					/>
				))}
			{`</${tag}>`}
		</>
	);
}
